import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';

/**
 * Loaders
 */
const loadingBarElement = document.querySelector('.loading-bar');
let sceneReady = false;

const loadingManager = new THREE.LoadingManager(
    () => {
        setTimeout(() => {
            gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0, delay: 1 });
            loadingBarElement.classList.add('ended');
            loadingBarElement.style.transform = '';
        }, 500);

        setTimeout(() => {
            sceneReady = true;
        }, 2000);
    },
    (itemUrl, itemsLoaded, itemsTotal) => {
        const progressRatio = itemsLoaded / itemsTotal;
        loadingBarElement.style.transform = `scaleX(${progressRatio})`;
    }
);

const gltfLoader = new GLTFLoader(loadingManager);
const cubeTextureLoader = new THREE.CubeTextureLoader(loadingManager);

/**
 * Base Setup
 */
// Canvas and Scene
const canvas = document.querySelector('canvas.webgl');
const scene = new THREE.Scene();

/**
 * Overlay
 */
const overlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: { uAlpha: { value: 1 } },
    vertexShader: `
        void main() {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform float uAlpha;
        void main() {
            gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
        }
    `
});

const overlay = new THREE.Mesh(new THREE.PlaneGeometry(2, 2, 1, 1), overlayMaterial);
scene.add(overlay);

/**
 * Update Materials
 */
const updateAllMaterials = () => {
    scene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = debugObject.envMapIntensity;
            child.material.needsUpdate = true;
            child.castShadow = true;
            child.receiveShadow = true;
        }
    });
};

/**
 * Environment Map
 */
const environmentMap = cubeTextureLoader.load([
    '/textures/environmentMaps/1/px.jpg',
    '/textures/environmentMaps/1/nx.jpg',
    '/textures/environmentMaps/1/py.jpg',
    '/textures/environmentMaps/1/ny.jpg',
    '/textures/environmentMaps/1/pz.jpg',
    '/textures/environmentMaps/1/nz.jpg'
]);

environmentMap.colorSpace = THREE.SRGBColorSpace;
scene.background = environmentMap;
scene.environment = environmentMap;

const debugObject = { envMapIntensity: 2.5 };

/**
 * Models
 */
gltfLoader.load('/models/DamagedHelmet/glTF/LEDHunblo.gltf', (gltf) => {
    gltf.scene.scale.set(2.5, 2.5, 2.5);
    gltf.scene.rotation.y = Math.PI * 0.5;
    scene.add(gltf.scene);
    updateAllMaterials();
});

/**
 * Points of Interest
 */
const raycaster = new THREE.Raycaster();
const points = [
    { position: new THREE.Vector3(-1.5, 0.5, -0.6), element: document.querySelector('.point-0') },
    { position: new THREE.Vector3(0.5, 0.5, -0.6), element: document.querySelector('.point-1') },
    { position: new THREE.Vector3(-10, 0.5, -0.7), element: document.querySelector('.point-2') }
];

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight('#ffffff', 3);
directionalLight.castShadow = true;
directionalLight.shadow.camera.far = 15;
directionalLight.shadow.mapSize.set(1024, 1024);
directionalLight.shadow.normalBias = 0.05;
directionalLight.position.set(0.25, 3, -2.25);
scene.add(directionalLight);

/**
 * Sizes
 */
const sizes = { width: window.innerWidth, height: window.innerHeight };

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;

    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();

    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
camera.position.set(4, 1, -4);
scene.add(camera);

const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.toneMapping = THREE.ReinhardToneMapping;
renderer.toneMappingExposure = 3;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animation
 */
const tick = () => {
    controls.update();

    if (sceneReady) {
        points.forEach((point) => {
            const screenPosition = point.position.clone().project(camera);
            raycaster.setFromCamera(screenPosition, camera);

            const intersects = raycaster.intersectObjects(scene.children, true);
            const intersectionDistance = intersects.length ? intersects[0].distance : Infinity;
            const pointDistance = point.position.distanceTo(camera.position);

            if (intersectionDistance < pointDistance) {
                point.element.classList.remove('visible');
            } else {
                point.element.classList.add('visible');
            }

            const translateX = screenPosition.x * sizes.width * 0.5;
            const translateY = -screenPosition.y * sizes.height * 0.5;
            point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
        });
    }

    renderer.render(scene, camera);
    window.requestAnimationFrame(tick);
};

tick();
